import React from "react";
import Menu from "../Menu";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useState, useEffect } from "react";
import SuccessMessage from "../../components/successMessage";
import FailedMessage from "../../components/FailedMessage";
import useApiUrls from "../../useApiUrls";

const UpdateAudio = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { getUrlByOperation, error: urlError } = useApiUrls();

  // State to hold form data
  const [formData, setFormData] = useState({
    id: id,
    file: null,
    fileName: "",
    name: "",
    active: true,
    remark: ""
  });

  // State to hold API response or error
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (id) {
      const fetchAudioData = async () => {
        const token = getCookie("token");
        const userid = getCookie("userid");
    
        if (!token || !userid) {
          console.error("Missing token or userId in cookies");
          return;
        }
  
        try {
          setIsLoading(true);
          // Use the URL from useApiUrls with fallback
          const audioSearchUrl = getUrlByOperation("AudioSearch") || 
            "https://api.dqr-display-tms.eventum.co.in/api/AudioPackOperation/SubUser/Search";
          
          const response = await axios.post(
            audioSearchUrl,
            { id: id },
            {
              headers: {
                "X-Authorization": token,
                UserId: userid,
                "Content-Type": "application/json-patch+json",
              },
            }
          );
  
          // Log the response for debugging
          console.log("Response from AudioSearch:", response);
  
          // Check if the data is in the expected format
          if (response.data && response.data.data && Array.isArray(response.data.data) && response.data.data.length > 0) {
            const audioData = response.data.data[0];
            setFormData({
              id: audioData.id,
              name: audioData.name || "",
              active: audioData.active === "Active" || audioData.active === true,
              file: null, // Can't set file input value for security reasons
              fileName: audioData.fileName || "", // Store the current file name
              remark: audioData.remark || ""
            });
          } else {
            // setMessage("No data found for the specified id.");
          }
        } catch (error) {
          console.error("Error fetching audio data:", error);
          setMessage("Failed to fetch audio data.");
        } finally {
          setIsLoading(false);
        }
      };
  
      fetchAudioData();
    }
  }, [id, getUrlByOperation]);
  
  // Handle input change
  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    
    if (type === "file" && files && files.length > 0) {
      // For file input, store the file and its name
      setFormData((prevData) => ({
        ...prevData,
        file: files[0],
        fileName: files[0].name
      }));
    } else if (name === "active") {
      // For active dropdown
      setFormData((prevData) => ({
        ...prevData,
        [name]: value === "Active"
      }));
    } else {
      // For all other inputs
      setFormData((prevData) => ({
        ...prevData,
        [name]: value
      }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = getCookie("token");
    const userid = getCookie("userid");

    if (!token || !userid) {
      setMessage("Missing authentication information. Please log in again.");
      return;
    }

    // Get the update URL from the hook or use the default
    const audioUpdateUrl = getUrlByOperation("AudioUpdate") || 
      "https://api.dqr-display-tms.eventum.co.in/api/AudioPackOperation/SubUser/Update";

    // Create FormData object for file upload
    const updateData = new FormData();
    updateData.append("id", formData.id);
    updateData.append("name", formData.name);
    updateData.append("active", formData.active);
    updateData.append("remark", formData.remark || "");
    
    // Only append file if a new one was selected
    if (formData.file) {
      updateData.append("file", formData.file);
    }

    try {
      setIsLoading(true);
      
      // Check if we should use FormData or JSON
      const useFormData = formData.file !== null;
      
      let response;
      if (useFormData) {
        // Use FormData for file uploads
        response = await axios.post(
          audioUpdateUrl,
          updateData,
          {
            headers: {
              "X-Authorization": token,
              UserId: userid,
              "Content-Type": "multipart/form-data"
            },
          }
        );
      } else {
        // Use JSON for updates without file changes
        const jsonData = {
          id: formData.id,
          name: formData.name,
          active: formData.active,
          remark: formData.remark || ""
        };
        
        response = await axios.post(
          audioUpdateUrl,
          jsonData,
          {
            headers: {
              "X-Authorization": token,
              UserId: userid,
              "Content-Type": "application/json-patch+json",
            },
          }
        );
      }

      console.log("Update response:", response);
      setMessage("Audio updated successfully!");

      // Set a delay before navigating
      setTimeout(() => {
        navigate("/audioPackage");
      }, 3000);
    } catch (error) {
      console.error("Update error:", error);
      setMessage("Failed to update audio. " + (error.response?.data?.message || error.message));
    } finally {
      setIsLoading(false);
    }
  };

  // If URLs have error, show it
  if (urlError) {
    return <div className="error-message">Error loading API URLs: {urlError}</div>;
  }

  return (
    <>
      <Menu />
      <div className="wrapper">
        <div className="content-wrapper">
          <section className="content-header">
            <div className="container-fluid">
              <div className="row mb-2">
                <div className="col-sm-6">
                  <h1>{id ? "Update" : "Add"} Audio</h1>
                </div>
              </div>
            </div>
          </section>

          <section className="content">
            <div className="container-fluid">
              <div className="row">
                <div className="col-md-12">
                  <div className="card card-info">
                    <div className="card-header">
                      <h3 className="card-title">
                        {id ? "Update" : "Add"} Audio
                      </h3>
                    </div>
                    <div className="card-body">
                      {isLoading ? (
                        <div className="text-center">
                          <p>Loading...</p>
                        </div>
                      ) : (
                        <form id="add-items" onSubmit={handleSubmit}>
                          <div className="row">
                            <div className="col-sm-6">
                              <div className="form-group">
                                <label>Name *</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={formData.name}
                                  onChange={handleChange}
                                  placeholder="Enter audio name"
                                  name="name"
                                  required
                                />
                              </div>
                            </div>
                            <div className="col-sm-6">
                              <div className="form-group">
                                <label>Audio File {id && !formData.file && formData.fileName && `(Current: ${formData.fileName})`}</label>
                                <input
                                  type="file"
                                  className="form-control"
                                  onChange={handleChange}
                                  name="file"
                                  accept="audio/*"
                                />
                                {id && (
                                  <small className="form-text text-muted">
                                    Leave empty to keep the current file
                                  </small>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-sm-6">
                              <div className="form-group">
                                <label>Active</label>
                                <select
                                  className="form-control"
                                  name="active"
                                  value={formData.active ? "Active" : "Deactive"}
                                  onChange={handleChange}
                                >
                                  <option value="Active">Active</option>
                                  <option value="Deactive">Deactive</option>
                                </select>
                              </div>
                            </div>
                            <div className="col-sm-6">
                              <div className="form-group">
                                <label>Remark</label>
                                <textarea
                                  className="form-control"
                                  value={formData.remark}
                                  onChange={handleChange}
                                  placeholder="Enter remarks"
                                  name="remark"
                                  rows="3"
                                />
                              </div>
                            </div>
                          </div>

                          <div className="form-footer">
                            <button
                              type="button"
                              className="btn btn-default mr-2"
                              onClick={() => navigate("/audio")}
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              id="add-sub"
                              className="btn btn-info"
                              disabled={isLoading}
                            >
                              {isLoading ? "Processing..." : (id ? "Update" : "Add")}
                            </button>
                          </div>
                        </form>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
      {message === "Audio updated successfully!" ? (
        <SuccessMessage message={message} />
      ) : message && message.includes("Failed") ? (
        <FailedMessage message={message} />
      ) : message ? (
        <div className="alert alert-info">{message}</div>
      ) : null}
    </>
  );
};

export default UpdateAudio;

// Helper function to get cookies
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}